"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { getStudentInfo, sendEnrollmentRequest } from "./api"
import { postAws } from "../aws"
import { generatePresignedUrl } from "../postFileAws"
import {
  BookOpen,
  Calendar,
  Clock,
  Edit,
  FileText,
  Loader2,
  Mail,
  Phone,
  Plus,
  Trash2,
  User,
  Users,
  X,
  UserPlus,
  Check,
} from "lucide-react"

export default function Dashboard() {
  const navigate = useNavigate()
  const [enrolledClasses, setEnrolledClasses] = useState([])
  const [enrollmentRequests, setEnrollmentRequests] = useState([])
  const [newClassCode, setNewClassCode] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [isEnrolling, setIsEnrolling] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [flashMessage, setFlashMessage] = useState(null)
  const [studentFetchData, setStudentFetchData] = useState()
  const [profileImageUrl, setProfileImageUrl] = useState("")

  // Organization request states
  const [showOrgRequestForm, setShowOrgRequestForm] = useState(false)
  const [orgRequestData, setOrgRequestData] = useState({
    organisationId: "",
  })
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false)
  const [requestMessage, setRequestMessage] = useState(null)

  const enrollmentFormRef = useRef(null)

  const fetchStudentInfo = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await getStudentInfo()
      if (response.statusCode === 200 || response.statusCode === 201) {
        const data = response.body.data
        setStudentFetchData(data)
        setEnrolledClasses(Array.isArray(data.enrolledClasses) ? data.enrolledClasses : [])
        setEnrollmentRequests(Array.isArray(data.enrollmentRequest) ? data.enrollmentRequest : [])

        // If organization is approved, save to local storage
        if (data.enrollmentRequestToOrg === "approved" && data.organisationId && data.organisationName) {
          localStorage.setItem("organisationId", data.organisationId)
          localStorage.setItem("organisationName", data.organisationName)
        }

        if (data.profilePhotoS3Uri) {
          const presignedUrl = await generatePresignedUrl(data.profilePhotoS3Uri)
          setProfileImageUrl(presignedUrl)
        }
      } else {
        throw new Error(response.body.message || "Failed to fetch student information")
      }
    } catch (error) {
      console.error("Error fetching student info:", error)
      setFlashMessage({
        type: "error",
        message: error.message || "Failed to fetch student information. Please try again.",
      })
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStudentInfo()
  }, [fetchStudentInfo])

  const handleContact = () => {
    navigate("/Communication")
  }

  const handleEnrollmentRequest = async (e) => {
    e.preventDefault()
    setIsEnrolling(true)
    try {
      const response = await sendEnrollmentRequest(newClassCode)
      console.log("Full response:", response)
      if (response.statusCode === 201) {
        setFlashMessage({ type: "success", message: response.message || "Enrollment request sent successfully" })
        setNewClassCode("")
        setEnrollmentRequests((prevRequests) => [
          ...prevRequests,
          {
            classCode: newClassCode,
            requestedAt: new Date().toISOString(),
            className: "Pending",
            subject: "Pending",
            teacherName: "Pending",
            enrollmentId: Date.now(),
          },
        ])
        await fetchStudentInfo()
      } else {
        throw new Error(response.message || "Failed to send enrollment request")
      }
    } catch (error) {
      console.error("Error sending enrollment request:", error)
      setFlashMessage({
        type: "error",
        message: error.message || "Failed to send enrollment request. Please try again.",
      })
    } finally {
      setIsEnrolling(false)
    }
  }

  const handleDeleteRequest = (classCode) => {
    setConfirmDelete(classCode)
  }

  const confirmDeleteRequest = async (enrollmentId) => {
    setIsDeleting(true)
    try {
      const deleteRequest = {
        body: { enrollmentId },
        request: "delete",
      }
      const response = await postAws(`https://api-dev.aurait.ai/enrollment`, "POST", deleteRequest)
      if (response && (response.statusCode === 200 || response.statusCode === 201)) {
        setFlashMessage({ type: "success", message: response.message || "Enrollment request deleted successfully" })
        setEnrollmentRequests((prevRequests) => prevRequests.filter((req) => req.enrollmentId !== enrollmentId))
      } else {
        throw new Error(response?.message || "Failed to delete enrollment request")
      }
    } catch (error) {
      console.error("Error deleting enrollment request:", error)
      setFlashMessage({
        type: "error",
        message: error.message || "Failed to delete enrollment request. Please try again.",
      })
    } finally {
      setIsDeleting(false)
      setConfirmDelete(null)
    }
  }

  const cancelDeleteRequest = () => {
    setConfirmDelete(null)
  }

  // Organization request handling
  const confirmOrgRequest = () => {
    setOrgRequestData({
      ...orgRequestData,
      confirmed: true,
    })
    // Re-submit the form with confirmation
    handleOrgRequestSubmit(new Event("submit"))
  }

  const handleOrgRequestSubmit = async (e) => {
    e.preventDefault()
    setIsSubmittingRequest(true)

    try {
      const studentId = localStorage.getItem("studentId")
      const hasEnrolledClasses = enrolledClasses.length > 0

      // If student has enrolled classes, show confirmation dialog first
      if (hasEnrolledClasses && !orgRequestData.confirmed) {
        setRequestMessage({
          type: "warning",
          text: "Warning: If you attach yourself to an organization, your current data will be removed. We recommend creating a new account with a different ID to join an organization instead.",
          needsConfirmation: true,
        })
        setIsSubmittingRequest(false)
        return
      }

      const requestData = {
        request: "student-admin-enroll",
        body: {
          userId: studentId,
          name: studentFetchData.studentName,
          email: studentFetchData.studentEmail,
          phone: studentFetchData.phoneNumber,
          organisationId: orgRequestData.organisationId,
        },
      }

      const response = await postAws("https://api-dev.aurait.ai/orgnaisational-admin", "POST", requestData)

      if (response.statusCode === 200) {
        setRequestMessage({
          type: "success",
          text: "Enrollment request sent successfully! You will be notified when approved.",
        })
        setFlashMessage({
          type: "success",
          message: "Enrollment request sent successfully!",
        })

        // Refresh student data to get updated enrollment status
        fetchStudentInfo()
        setShowOrgRequestForm(false)
      } else if (response.body?.error?.includes("organisation") || response.body?.message?.includes("organisation")) {
        // Handle case when organization doesn't exist
        setRequestMessage({
          type: "error",
          text: "No such organisation exists. Please check the ID and try again.",
          allowRetry: true,
        })
      } else {
        throw new Error(response.body?.error || "Failed to send enrollment request")
      }
    } catch (error) {
      console.error("Error sending enrollment request:", error)
      setRequestMessage({
        type: "error",
        text: error.message || "Failed to send enrollment request. Please try again.",
        allowRetry: true,
      })
      setFlashMessage({
        type: "error",
        message: error.message || "Failed to send enrollment request. Please try again.",
      })
    } finally {
      setIsSubmittingRequest(false)
    }
  }

  useEffect(() => {
    if (flashMessage) {
      const timer = setTimeout(() => {
        setFlashMessage(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [flashMessage])

  const handleViewAnalytics = () => {
    navigate("/student/details")
  }

  const handleProfileEdit = () => {
    navigate("/student/profile-edit")
  }

  const handleJoinClass = () => {
    enrollmentFormRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 text-indigo-600 animate-spin" />
          <p className="text-lg font-medium text-indigo-700">Loading your dashboard...</p>
        </div>
      </div>
    )
  }

  // Check if student has an organization attached
  const hasOrganization =
    (studentFetchData?.organisationId &&
      studentFetchData?.organisationName &&
      studentFetchData?.enrollmentRequestToOrg === "approved") ||
    localStorage.getItem("organisationId")
  const hasPendingRequest = studentFetchData?.enrollmentRequestToOrg === "sent"
  const hasRejectedRequest = studentFetchData?.enrollmentRequestToOrg === "rejected"

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen md:p-2">
      {/* Flash Message */}
      {flashMessage && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 ${
            flashMessage.type === "error" ? "bg-red-500" : "bg-emerald-500"
          } text-white transition-all duration-500 transform animate-fade-in-down`}
        >
          <span>{flashMessage.message}</span>
          <button
            onClick={() => setFlashMessage(null)}
            className="p-1 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Organization Request Modal */}
      {showOrgRequestForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Join Organisation</h3>
                <button
                  onClick={() => {
                    setShowOrgRequestForm(false)
                    setRequestMessage(null)
                    setOrgRequestData({ organisationId: "" })
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {requestMessage ? (
                <div
                  className={`p-4 rounded-md mb-4 ${
                    requestMessage.type === "success"
                      ? "bg-green-50 text-green-800"
                      : requestMessage.type === "warning"
                        ? "bg-yellow-50 text-yellow-800"
                        : "bg-red-50 text-red-800"
                  }`}
                >
                  <p className="text-sm font-medium">{requestMessage.text}</p>

                  {requestMessage.needsConfirmation && (
                    <div className="mt-4 flex space-x-3">
                      <button
                        onClick={() => setRequestMessage(null)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={confirmOrgRequest}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium"
                      >
                        Yes, Continue Anyway
                      </button>
                    </div>
                  )}

                  {requestMessage.allowRetry && (
                    <div className="mt-4 flex space-x-3">
                      <button
                        onClick={() => setRequestMessage(null)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                      >
                        Try Again
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleOrgRequestSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="organisationId" className="block text-sm font-medium text-gray-700 mb-2">
                      Organisation ID
                    </label>
                    <input
                      type="text"
                      id="organisationId"
                      name="organisationId"
                      value={orgRequestData.organisationId}
                      onChange={(e) => setOrgRequestData({ ...orgRequestData, organisationId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Enter organisation ID"
                      required
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowOrgRequestForm(false)
                        setRequestMessage(null)
                        setOrgRequestData({ organisationId: "" })
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmittingRequest}
                      className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-75"
                    >
                      {isSubmittingRequest ? (
                        <>
                          <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                          Sending...
                        </>
                      ) : (
                        "Send Request"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header with Student Info */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
          <div className="relative">
            <div className="h-24"></div>
            <div className="absolute top-4 right-4 flex gap-2">
              <button
                onClick={handleProfileEdit}
                className="bg-purple-500 hover:bg-purple-600 text-white p-2 rounded-full transition-all duration-300 shadow-lg"
                aria-label="Edit Profile"
              >
                <Edit className="h-5 w-5" />
              </button>
            </div>

            <div className="relative px-6 -mt-16">
              <div className="flex flex-row gap-4 items-start">
                {/* Profile Image - smaller on mobile */}
                <div className="relative">
                  {profileImageUrl ? (
                    <img
                      className="h-20 w-20 md:h-32 md:w-32 rounded-full border-4 border-white shadow-lg object-cover"
                      src={profileImageUrl || "/placeholder.svg"}
                      alt={studentFetchData?.studentName}
                    />
                  ) : (
                    <div className="h-20 w-20 md:h-32 md:w-32 rounded-full border-4 border-white shadow-lg bg-gray-200 flex items-center justify-center">
                      <User className="h-10 w-10 md:h-16 md:w-16 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Student Info - positioned to the right of profile image */}
                <div className="pb-5 flex-grow">
                  <h1 className="text-xl md:text-3xl font-bold text-gray-800">{studentFetchData?.studentName}</h1>
                  <div className="mt-2 flex flex-col gap-1 md:gap-4">
                    <div className="flex items-center gap-2 text-sm md:text-base text-gray-600">
                      <Mail className="h-4 w-4 text-indigo-500" />
                      <span className="truncate">{studentFetchData?.studentEmail}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm md:text-base text-gray-600">
                      <Phone className="h-4 w-4 text-indigo-500" />
                      <span>{studentFetchData?.phoneNumber}</span>
                    </div>

                    {/* Organization Status Section */}
                    <div className="flex items-center gap-2 text-sm md:text-base text-gray-600">
                      <UserPlus className="h-4 w-4 text-indigo-500" />
                      <div className="flex items-center flex-wrap gap-2">
                        {hasOrganization ? (
                          <>
                            <span>Organisation: {studentFetchData?.organisationName || "Not Available"}</span>
                            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full flex items-center gap-1">
                              <Check className="h-3 w-3" />
                              Joined
                            </span>
                          </>
                        ) : hasPendingRequest ? (
                          <>
                            <span>Organisation: {studentFetchData?.organisationName || "Not Available"}</span>
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full flex items-center gap-1">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Pending
                            </span>
                          </>
                        ) : hasRejectedRequest ? (
                          <>
                            <span>Organisation: {studentFetchData?.organisationName || "Rejected"}</span>
                            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full flex items-center gap-1">
                              <X className="h-3 w-3" />
                              Rejected
                            </span>
                            <button
                              onClick={() => setShowOrgRequestForm(true)}
                              className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded-full hover:bg-indigo-200 transition-colors"
                            >
                              Try Again
                            </button>
                          </>
                        ) : (
                          <>
                            <span>Organisation: Not Attached</span>
                            <button
                              onClick={() => setShowOrgRequestForm(true)}
                              className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded-full hover:bg-indigo-200 transition-colors flex items-center gap-1"
                            >
                              <Plus className="h-3 w-3" />
                              Join Organisation
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons - moved outside the profile section */}

            </div>

            {/* Parent Information */}
            <div className="px-6 py-5 border-t border-gray-100">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-indigo-500" />
                  <h3 className="text-lg font-semibold text-gray-800">Parent Information</h3>
                </div>

                {/* Action Buttons on same line as heading on larger screens */}

              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col">
                  <span className="text-sm text-gray-500">Name</span>
                  <span className="text-gray-700">{studentFetchData?.parentName || "Not provided"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-500">Email</span>
                  <span className="text-gray-700">{studentFetchData?.parentEmail || "Not provided"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-500">Phone</span>
                  <span className="text-gray-700">{studentFetchData?.parentNumber || "Not provided"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Enrolled Classes */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-indigo-600" />
                <h2 className="text-2xl font-bold text-gray-800">Enrolled Classes</h2>
              </div>
            </div>

            {enrolledClasses.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {enrolledClasses.map((classInfo, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="h-3 bg-gradient-to-r from-indigo-500 to-purple-500"></div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-end">
                          <span className="text-3xl font-bold text-indigo-600">{classInfo.classValue}</span>
                          <span className="text-2xl font-medium text-gray-700 ml-1">{classInfo.section}</span>
                        </div>
                        <div className="ml-auto px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                          {classInfo.subject}
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-700">{classInfo.teacherName}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-700">Code: {classInfo.classCode}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">You are not enrolled in any classes yet.</p>
                <p className="text-gray-500 text-sm mt-2">Use the form below to enroll in a class.</p>
              </div>
            )}
          </div>

          {/* Pending Enrollment Requests */}


          {/* Enroll in a New Class */}

        </div>
      </div>
    </div>
  )
}
